import React from 'react';

import { useSelector } from 'react-redux';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// the getReactionStats function returns an array of objects containing the count of each reaction and the
// ids of the users who reacted with that reaction
function getReactionStats(reactions) {
	const reactionStats = [];

	reactions.forEach((reactionsObj) => {
		const { _user, emoji } = reactionsObj;
		const existingReactionIndex = reactionStats.findIndex(
			(stat) => stat.emoji === emoji
		);

		if (existingReactionIndex !== -1) {
			// If the reaction already exists in reactionStats, update the count and users array
			reactionStats[existingReactionIndex].count += 1;
			reactionStats[existingReactionIndex].users.push(_user);
		} else {
			// If the reaction does not exist, create a new entry with the current user
			reactionStats.push({ emoji, count: 1, users: [_user] });
		}
	});

	return reactionStats;
}

function getReactionListofUsers(emoji, users) {
	if (!emoji || !users) return '';
	if (users.length === 0) return '';

	if (users.length === 1) return `${users[0].name} reacted with ${emoji}`;
	const userList = users.map((user) => user.name); // Extracting user names
	const lastUser = userList.pop(); // Removing the last user from the array

	// Joining all user names with commas and adding "and" before the last user
	const userString = userList.join(', ');

	return `${userString} and ${lastUser} reacted with ${emoji}`;
}

const Reaction = ({ _users, emoji, count, onReactionClick, isMine }) => {
	const users = useSelector((state) => state.users);
	const getUsers = () => {
		return users.filter((user) => _users.includes(user._id));
	};
	const who = getReactionListofUsers(emoji, getUsers());

	return (
		<Tooltip delayDuration={0}>
			<TooltipTrigger asChild>
				<button
					type="button"
					onClick={() => onReactionClick(emoji)}
					// the tooltip needs a hover, so screen readers and phones get the names here
					aria-label={who || emoji}
					aria-pressed={isMine}
					className={cn(
						'flex h-6 items-center gap-1 rounded-full border px-2 text-xs shadow-[0_1px_2px_rgba(0,0,0,.04)] transition-colors',
						// phones: a bigger chip with a 44px tall hit area
						'max-md:relative max-md:h-8 max-md:px-2.5 max-md:text-[13px] max-md:before:absolute max-md:before:-inset-x-0.5 max-md:before:-inset-y-1.5',
						isMine
							? 'border-primary/40 bg-primary/10 text-foreground'
							: 'border-border/70 bg-background text-muted-foreground hover:bg-muted'
					)}
				>
					<span className="text-[13px] leading-none max-md:text-base" aria-hidden="true">
						{emoji}
					</span>
					{count > 1 && (
						<span className="tabular-nums" aria-hidden="true">
							{count}
						</span>
					)}
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">{who}</TooltipContent>
		</Tooltip>
	);
};

// Reaction chips under a message. Your own reactions are highlighted and
// clicking one removes it.
const Reactions = ({
	onReactionClick,
	reactions,
	isAuthenticatedUser,
	_authenticatedUser,
}) => {
	return (
		<div
			className={cn(
				'mt-1 flex flex-wrap gap-1 px-1 max-md:mt-1.5 max-md:gap-1.5',
				isAuthenticatedUser ? 'justify-end' : 'justify-start'
			)}
		>
			{getReactionStats(reactions).map((reaction) => (
				<Reaction
					key={reaction.emoji}
					_users={reaction.users}
					emoji={reaction.emoji}
					count={reaction.count}
					isMine={reaction.users.includes(_authenticatedUser)}
					onReactionClick={onReactionClick}
				/>
			))}
		</div>
	);
};

export default Reactions;
